import React from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Warning banner that appears when tenant has been switched in another browser tab
 * Warns user that they need to reload to prevent data conflicts
 */
const TenantChangeWarning = ({ 
  visible, 
  originalTenantCode, 
  changedTenantCode, 
  onReload, 
  onDismiss 
}) => {
  const { t } = useTranslation();
  
  if (!visible) return null;
  
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-red-600 to-orange-600 shadow-lg"
      style={{ animation: 'slideDown 0.3s ease-out' }}
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <AlertTriangle className="h-6 w-6 text-white flex-shrink-0 animate-pulse" />
            <div className="flex-1">
              <p className="text-white font-semibold text-sm md:text-base">
                {t('tenantChangeWarning.title', '⚠️ Tenant Changed in Another Tab')}
              </p>
              <p className="text-white/90 text-xs md:text-sm mt-1">
                {t('tenantChangeWarning.message', 
                  `Tenant was switched from "${originalTenantCode}" to "${changedTenantCode}" in another browser tab. Please reload this page to prevent data conflicts and save errors.`
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onReload}
              className="flex items-center gap-2 px-4 py-2 bg-white text-red-600 rounded-lg hover:bg-red-50 transition-colors font-semibold text-sm shadow-md"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">
                {t('tenantChangeWarning.reload', 'Reload Page')}
              </span>
              <span className="sm:hidden">
                {t('tenantChangeWarning.reloadShort', 'Reload')}
              </span>
            </button>
            
            <button
              onClick={onDismiss}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              title={t('tenantChangeWarning.dismiss', 'Dismiss warning (not recommended)')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default TenantChangeWarning;

